import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDecimal } from "./CondicionesFinancieras";
import { pagosApi } from "./pagosApi";

const valorActual = (fila) => fila?.propuesta?.porcentaje ?? fila?.vigente?.porcentaje ?? "";
const claveFila = (fila) => fila.clave || `${fila.tipo || "TASA"}|${fila.alcance || "GENERAL"}|${fila.sujeto || "*"}|${fila.parametro}`;

export const prepararItemTasa = (fila, porcentaje, fuente, motivo) => ({
  reglaId: fila?.propuesta?.id ?? fila?.vigente?.id ?? null,
  tipo: fila.tipo, alcance: fila.alcance, sujetos: [fila.sujeto || "*"], parametro: fila.parametro, porcentaje,
  prioridad: fila?.propuesta?.prioridad ?? fila?.vigente?.prioridad ?? 0, fuente, motivo,
});

const etiquetaEstado = (fila) => ({
  APROBADA: "Aplicada", PENDIENTE_APROBACION: "Lista para aplicar", BORRADOR: "Lista para aplicar",
  CONFLICTO: "Conflicto", FALTA: "Falta cargar", SIN_USO: "Sin operaciones",
}[fila.situacion] || fila.situacion || "Sin tasa");
const numero = (valor) => Number(String(valor ?? "").trim().replace(",", "."));
const distinto = (a, b) => Number.isFinite(numero(a)) && numero(a) !== numero(b);
const esTasaMaximaDiaria = (fila) => fila?.tipo === "TASA_ANTICIPO_CONVERSOR"
  && fila?.alcance === "GENERAL"
  && /^(VISA|MASTERCARD):CONTADO_SIN_TNA_NI_TF$/i.test(String(fila?.parametro || ""));

export default function TasasDelDia({ token, fecha, corridaId, onActualizada }) {
  const [tablero, setTablero] = useState(null);
  const [ediciones, setEdiciones] = useState({});
  const [fuente, setFuente] = useState("");
  const [motivo, setMotivo] = useState("Configuración financiera del día");
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState("");
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    if (!fecha) return;
    setCargando("cargar"); setError("");
    try {
      const data = await pagosApi.tasasDiaTablero(fecha, corridaId, token);
      setTablero(data);
      setEdiciones(Object.fromEntries((data?.filas || []).map((fila) => [claveFila(fila), valorActual(fila)])));
    } catch (e) { setError(e?.message || "No se pudo cargar la configuración del día."); }
    finally { setCargando(""); }
  }, [fecha, corridaId, token]);
  useEffect(() => { cargar(); }, [cargar]);

  // Este bloque resuelve exclusivamente la tasa máxima diaria. Las demás condiciones
  // financieras se muestran una sola vez en "Otras decisiones del día".
  const filas = useMemo(() => (tablero?.filas || []).filter(esTasaMaximaDiaria), [tablero]);
  const modificadas = filas.filter((fila) => String(ediciones[claveFila(fila)] || "").trim()
    && (distinto(ediciones[claveFila(fila)], valorActual(fila))
      || fila.situacion === "PENDIENTE_APROBACION" || fila.situacion === "BORRADOR"
      || (!fila.propuesta && fila.situacion !== "APROBADA")));
  const guardar = async () => {
    setError(""); setResultado(null);
    if (!fuente.trim()) return setError("Indicá la fuente común de la configuración del día.");
    if (!motivo.trim()) return setError("Indicá el motivo de la configuración.");
    if (!modificadas.length) return setError("Ingresá o modificá al menos una tasa.");
    setCargando("guardar");
    try {
      const respuesta = await pagosApi.tasasDiaGuardar({ fecha, corridaId, fuente, motivo,
        items: modificadas.map((fila) => prepararItemTasa(fila, ediciones[claveFila(fila)], fuente, motivo)) }, token);
      setResultado(respuesta);
      const corridaAplicada = respuesta?.corrida?.id || corridaId;
      if (onActualizada) await onActualizada(corridaAplicada);
      else await cargar();
    } catch (e) { setError(e?.message || "No se pudo guardar la configuración del día."); }
    finally { setCargando(""); }
  };

  const copiar = async () => {
    setCargando("copiar"); setError("");
    try {
      setResultado(await pagosApi.tasasDiaCopiar({ origen: String(tablero.diaAnterior).slice(0, 10), destino: fecha }, token));
      await cargar();
    } catch (e) { setError(e?.message || "No se pudo copiar la configuración anterior."); }
    finally { setCargando(""); }
  };

  return <section className="pagos-daily-rate-card" aria-label="Tasas del día">
    <div className="pagos-section-heading"><div><h3>Tasa máxima diaria</h3>
      <p>Sólo Visa y Mastercard con plan de cuotas vacío y descuento financiero vacío. Al guardar se aplica y recalcula el lote.</p></div>
      <span className="pagos-status pagos-status--safe">Se aplica al guardar</span></div>
    {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}
    {resultado && <div className={`pagos-alert ${(resultado.errores || []).length ? "pagos-alert--error" : "pagos-alert--info"}`} role="status">
      {(resultado.correctas || []).length} aplicadas y lote recalculado. {(resultado.omitidas || []).length} omitidas. {(resultado.errores || []).map((item) => item.mensaje).join(" ")}
    </div>}
    <div className="pagos-daily-common">
      <label><span>Fuente del día</span><input aria-label="Fuente común de tasas" value={fuente} onChange={(e) => setFuente(e.target.value)} placeholder="Mail o comunicación de Finanzas" /></label>
      <label><span>Motivo</span><input aria-label="Motivo común de tasas" value={motivo} onChange={(e) => setMotivo(e.target.value)} /></label>
    </div>
    <div className="pagos-table-wrap"><table aria-label="Configuración masiva de tasas"><thead><tr>
      <th>Marca</th><th>Tipo de operación</th><th>Plan o caso</th><th>Grupo / comercio</th><th>Tasa actual</th><th>Nueva tasa (%)</th><th>Operaciones</th><th>Estado</th>
    </tr></thead><tbody>{filas.map((fila) => <tr key={claveFila(fila)} className={fila.situacion === "CONFLICTO" || fila.situacion === "FALTA" ? "pagos-row--review" : ""}>
      <td>{fila.tarjeta || "Todas"}</td><td>{fila.tipoOperacion || fila.concepto}</td><td>{fila.plan || fila.parametro}</td>
      <td>{fila.sujetoNombre || (fila.alcance === "GENERAL" ? "General" : fila.sujeto)}</td>
      <td>{valorActual(fila) ? `${valorActual(fila)} %` : "—"}</td>
      <td><input aria-label={`Nueva tasa ${claveFila(fila)}`} inputMode="decimal" value={ediciones[claveFila(fila)] ?? ""}
        onChange={(e) => setEdiciones((actual) => ({ ...actual, [claveFila(fila)]: e.target.value }))} placeholder="0,00" /></td>
      <td>{fila.operaciones ?? "—"}<small className="pagos-cell-note">{fila.bruto ? formatDecimal(fila.bruto) : ""}</small></td>
      <td><span className={`pagos-status ${fila.situacion === "APROBADA" ? "pagos-status--safe" : "pagos-status--pending"}`}>{etiquetaEstado(fila)}</span></td>
    </tr>)}</tbody></table></div>
    <div className="pagos-rate-footer">
      <button type="button" className="pagos-link-button" disabled={!tablero || !!cargando} onClick={copiar}>Copiar configuración anterior como borrador</button>
      <div><button type="button" className="pagos-primary" disabled={!corridaId || !!cargando} onClick={guardar}>{cargando === "guardar" ? "Guardando, aplicando y recalculando…" : `Guardar y aplicar (${modificadas.length})`}</button></div>
    </div>
    <details className="pagos-advanced-detail"><summary>Ver alcance de la tasa máxima diaria ({(tablero?.operacionesElegibles || []).length})</summary>
      <p>Sólo Visa o Mastercard crédito, Venta contado, sin plan real y con descuento financiero ausente. La referencia E de Fiserv se conserva para auditoría y no se interpreta como plan.</p>
    </details>
  </section>;
}
